import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import { apiPost } from '../../shared/api/client';
import Card from '../../shared/components/Card';
import Button from '../../shared/components/Button';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { useAppStore } from '../../shared/stores/appStore';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

// Where the token waits while the invitee signs up. The server also claims
// invitations by email on every /babies load, so this only matters when they
// join with a different address than the one they were invited on — which is
// exactly what happens when they choose Sign in with Apple and Apple hands us a
// private-relay address instead.
export const PENDING_INVITE_KEY = 'bt_pending_invite';

export function stashInviteToken(token) {
  try {
    localStorage.setItem(PENDING_INVITE_KEY, token);
  } catch {
    /* private mode / quota — the email-match path still covers the common case */
  }
}

export function readInviteToken() {
  try {
    return localStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

export function clearInviteToken() {
  try {
    localStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    /* nothing to do */
  }
}

// The token is "<babyId>.<secret>" so the accept call knows which baby to
// target without a second round trip.
export function babyIdFromToken(token) {
  const first = String(token || '').split('.')[0];
  return first || null;
}

export async function acceptInviteToken(token) {
  const babyId = babyIdFromToken(token);
  if (!babyId) throw new Error('Invalid invitation link');
  return apiPost(`/babies/${babyId}/caregivers/accept`, { token });
}

/**
 * /invite/:token — the page the invitation email links to.
 *
 * It works signed-out on purpose: the person being invited usually has no
 * BabyTrack account yet, which is the entire reason the old flow dead-ended on
 * "No user found with that email". Shown before sign-in: who invited them and
 * which baby, and nothing else about that baby's data.
 */
export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const setSelectedBabyId = useAppStore((s) => s.setSelectedBabyId);
  const [authState, setAuthState] = useState('loading');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Auth.currentAuthenticatedUser()
      .then(() => !cancelled && setAuthState('authenticated'))
      .catch(() => !cancelled && setAuthState('unauthenticated'));
    return () => {
      cancelled = true;
    };
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/invites/${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error('Invitation not found');
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const result = await acceptInviteToken(token);
      clearInviteToken();
      if (result?.babyId) setSelectedBabyId(result.babyId);
      toast.success(
        result?.babyName ? `You can now help track ${result.babyName}` : 'Invitation accepted'
      );
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'We couldn’t accept that invitation.');
    } finally {
      setAccepting(false);
    }
  };

  const goSignUp = (path) => {
    stashInviteToken(token);
    navigate(path);
  };

  if (isLoading || authState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-surface-50 dark:bg-night-bg">
        <Card className="max-w-sm w-full text-center">
          <h1 className="text-lg font-bold mb-2">This invitation has expired</h1>
          <p className="text-sm text-gray-500 dark:text-night-muted mb-4">
            Invitations last 30 days, and each one can only be used once. Ask whoever invited
            you to send a new one.
          </p>
          <Link to="/login">
            <Button variant="secondary" fullWidth>
              Go to BabyTrack
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const who = data.inviterName || 'Someone';
  const babyName = data.babyName || 'their baby';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-surface-50 dark:bg-night-bg">
      <Card className="max-w-sm w-full text-center">
        <div className="text-4xl mb-3" aria-hidden="true">
          👶
        </div>
        <h1 className="text-lg font-bold mb-2">
          {who} invited you to help track {babyName}
        </h1>
        <p className="text-sm text-gray-500 dark:text-night-muted mb-5">
          As a caregiver you’ll be able to see and log feeds, sleep, nappies and milestones —
          so everyone looking after {babyName} is working from the same picture.
        </p>

        {authState === 'authenticated' ? (
          <Button fullWidth loading={accepting} onClick={handleAccept}>
            Accept invitation
          </Button>
        ) : (
          <div className="space-y-2">
            <Button fullWidth onClick={() => goSignUp('/register')}>
              Create a free account
            </Button>
            <Button variant="secondary" fullWidth onClick={() => goSignUp('/login')}>
              I already have an account
            </Button>
          </div>
        )}

        <p className="text-xs text-gray-400 dark:text-night-muted mt-4">
          BabyTrack is free to use, and you can step away at any time.
        </p>
      </Card>
    </div>
  );
}
